import { useEffect, useMemo, useState } from "react";
import useWebsites from "../../../data/useWebsites";
import { IWebsite } from "../../../models/Website";
import { useAuth } from "../../../contexts/AuthContext";
import { wilmaSAVCustomerId } from "../../../config";

function hexToRgba(hex: string, alpha: number) {
  const h = hex.replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const int = parseInt(full, 16);
  const r = (int >> 16) & 255;
  const g = (int >> 8) & 255;
  const b = int & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function stripDomain(raw: string): string {
  return raw.replace(/^https?:\/\/(www\.)?/, "").split("/")[0];
}

export default function QuoteWebsitePill({
  sourceCustomerId,
  sourceWebsiteId,
  websiteDomain,
  websitePrimaryColor,
  showLabel = true,
  fallbackLabel,
}: {
  sourceCustomerId: string | null;
  sourceWebsiteId?: string | null;
  // When the quote itself carries the website details (e.g. the paginated table DTO) pass them
  // through - the websites list is scoped to the logged in customer so SAV can't look them up.
  websiteDomain?: string | null;
  websitePrimaryColor?: string | null;
  showLabel?: boolean;
  // Shown as a neutral pill when the source website can't be resolved.
  fallbackLabel?: string;
}) {
  const { customerId } = useAuth();
  const { websitesQuery } = useWebsites();
  const [website, setWebsite] = useState<IWebsite | null>(null);

  const isSav = customerId === wilmaSAVCustomerId;
  const showAsSellAVehicle =
    sourceCustomerId === null || (!isSav && sourceCustomerId === wilmaSAVCustomerId);

  const skipLookup = showAsSellAVehicle || !!websiteDomain;

  useEffect(() => {
    if (skipLookup) return;
    if (!websitesQuery.isLoading && websitesQuery.data) {
      const websites = websitesQuery.data.data;
      const found =
        (sourceWebsiteId ? websites.find((w) => w.id === sourceWebsiteId) : undefined) ??
        websites.find((w) => w.customerId === sourceCustomerId);
      setWebsite(found || null);
    }
  }, [websitesQuery.data, sourceCustomerId, sourceWebsiteId, websitesQuery.isLoading, skipLookup]);

  const resolvedDomain = showAsSellAVehicle
    ? null
    : (websiteDomain ?? website?.domain ?? null);

  const brand =
    showAsSellAVehicle || !resolvedDomain
      ? "#6B7280"
      : ((websiteDomain ? websitePrimaryColor : website?.primaryColor) ?? "#6B7280");

  const styles = useMemo(
    () => ({
      backgroundColor: hexToRgba(brand, 0.10),
      borderColor: hexToRgba(brand, 0.25),
      color: brand,
    }),
    [brand]
  );

  if (!showAsSellAVehicle && !resolvedDomain && !fallbackLabel) return null;

  const displayText = showAsSellAVehicle
    ? "SellAVehicle"
    : resolvedDomain
      ? stripDomain(resolvedDomain)
      : fallbackLabel!;

  return (
    <span
      className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-medium border"
      style={styles}
      title={displayText}
    >
      {showLabel && <span className="text-xs opacity-80">Website</span>}
      <span className="truncate max-w-[180px] font-semibold">{displayText}</span>
    </span>
  );
}
